/**
 * R.U.S.H. sign-in against the K-Stack Nexus gateway.
 * Same-origin: per-app Caddy proxies /auth to nexus-dev and stamps the app key.
 * Registration is closed. Only the owner and the playtest account may hold a session.
 */

export const APP_ID = 'rush';
export const ALLOWED_EMAILS = ['ekiss71@hotmail.com', 'rush-playtest@kstack.app'] as const;
export const ALLOWED_EMAIL = ALLOWED_EMAILS[0];
const GATEWAY_BASE = '';

export class AuthError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
  }
}

export interface JwtPayload {
  sub?: string;
  email?: string;
  auth_method?: string;
  given_name?: string;
  [key: string]: unknown;
}

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/'))) as JwtPayload;
  } catch {
    return null;
  }
}

export function isAllowedEmail(email: string | undefined | null): boolean {
  const value = (email ?? '').trim().toLowerCase();
  return (ALLOWED_EMAILS as readonly string[]).includes(value);
}

/** Nexus access tokens carry auth_method. Cognito tokens do not. */
export function isNexusAccessToken(token: string | undefined | null): boolean {
  if (!token) return false;
  const method = decodeJwt(token)?.auth_method;
  return method === 'password' || method === 'oauth' || method === 'guest';
}

export interface SessionTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface SessionUser {
  userId: string;
  email: string;
  username: string;
  name?: string;
}

function personName(claims: JwtPayload | null): string | undefined {
  const name = typeof claims?.name === 'string' ? claims.name.trim() : '';
  if (name) return name;
  const given = typeof claims?.given_name === 'string' ? claims.given_name.trim() : '';
  const family = typeof claims?.family_name === 'string' ? claims.family_name.trim() : '';
  const composed = [given, family].filter(Boolean).join(' ');
  return composed || undefined;
}

export function userFromAccessToken(accessToken: string, idToken?: string): SessionUser | null {
  const claims = decodeJwt(accessToken);
  const email = typeof claims?.email === 'string' ? claims.email.trim().toLowerCase() : '';
  if (!email || !claims?.sub) return null;
  const name = personName(decodeJwt(idToken || '')) || personName(claims);
  return {
    userId: String(claims.sub),
    email,
    username: email.split('@')[0] || email,
    ...(name ? { name } : {}),
  };
}

export async function loginPassword(email: string, password: string): Promise<SessionTokens> {
  const res = await fetch(`${GATEWAY_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, appId: APP_ID }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    accessToken?: string;
    idToken?: string;
    refreshToken?: string;
    message?: string;
  };
  if (!res.ok || !body.accessToken) {
    throw new AuthError(res.status, body.message || 'Sign-in failed');
  }
  return {
    accessToken: body.accessToken,
    idToken: body.idToken || body.accessToken,
    refreshToken: body.refreshToken || '',
  };
}

export async function logoutSession(refreshToken: string | undefined): Promise<void> {
  if (!refreshToken) return;
  try {
    await fetch(`${GATEWAY_BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Local session is cleared either way.
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  await fetch(`${GATEWAY_BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(email: string, code: string, newPassword: string): Promise<void> {
  const res = await fetch(`${GATEWAY_BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, newPassword }),
  });
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  if (!res.ok) throw new AuthError(res.status, body.message || 'Could not reset password.');
}

function oauthStart(provider: 'google' | 'apple'): void {
  const redirectUri = window.location.origin + '/';
  const params = new URLSearchParams({ redirect_uri: redirectUri, app_id: APP_ID });
  window.location.assign(`${GATEWAY_BASE}/auth/oauth/${provider}?${params}`);
}

export function startGoogleOAuth(): void {
  oauthStart('google');
}

export function startAppleOAuth(): void {
  oauthStart('apple');
}

const OAUTH_ERROR_KEY = 'rush.oauthError';

export function takeOAuthError(): string | null {
  const message = sessionStorage.getItem(OAUTH_ERROR_KEY);
  if (message) sessionStorage.removeItem(OAUTH_ERROR_KEY);
  return message;
}

export interface OAuthCapture {
  tokens: SessionTokens;
  user: SessionUser;
}

/**
 * Read #oauth_result from a Google/Apple return and strip the fragment.
 * Writes nothing. The caller stores the session only after the allowlist check.
 */
export function consumeOAuthFragment(): OAuthCapture | { error: string } | null {
  const hash = window.location.hash || '';
  if (!hash.startsWith('#') || !hash.includes('oauth_result=')) return null;
  const params = new URLSearchParams(hash.slice(1));
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  if (params.get('oauth_result') === 'error') {
    return { error: params.get('oauth_error') || 'Sign-in failed.' };
  }
  if (params.get('oauth_result') !== 'success') return null;
  const accessToken = params.get('access_token') || '';
  const idToken = params.get('id_token') || accessToken;
  const refreshToken = params.get('refresh_token') || '';
  if (!accessToken) return { error: 'Sign-in did not return a session.' };
  const user = userFromAccessToken(accessToken, idToken);
  if (!user || !isAllowedEmail(user.email)) {
    return { error: 'This account is not authorized for R.U.S.H.' };
  }
  return { tokens: { accessToken, idToken, refreshToken }, user };
}

export function rememberOAuthError(message: string): void {
  sessionStorage.setItem(OAUTH_ERROR_KEY, message);
}
